import { detectImageFormat, inspectPng, inspectJpeg, stripPng, stripJpeg } from "./imageMeta";
import { detectContainerFormat, inspectContainer, cleanContainer } from "./containerMeta";
import { disruptPixels, isRasterFormat } from "./pixelScrub";
import { isVideoFormat, inspectVideo, cleanVideo } from "./videoMeta";

export const SUPPORTED_EXT = [
  ".png", ".jpg", ".jpeg", ".svg", ".pdf", ".docx", ".odt", ".html", ".htm",
  ".md", ".markdown", ".mdx", ".txt", ".csv", ".json", ".mp4", ".m4v", ".mov",
];

const MIME = { png: "image/png", jpeg: "image/jpeg" };

export function detectFormat(name, u8) {
  const img = detectImageFormat(u8);
  if (img !== "unknown") return img;
  if (isVideoFormat(name, u8)) return "video";
  return detectContainerFormat(name, u8);
}

export function inspectBytes(format, u8) {
  if (format === "png") { const r = inspectPng(u8); return { ...r, hasAiMetadata: r.hasAi }; }
  if (format === "jpeg") { const r = inspectJpeg(u8); return { ...r, hasAiMetadata: r.hasAi }; }
  if (format === "video") { const r = inspectVideo(u8); return { ...r, hasAiMetadata: r.hasAi }; }
  if (format === "unknown") return { hasC2pa: false, hasAi: false, findings: ["unsupported format"] };
  const r = inspectContainer(format, u8);
  return { ...r, hasAiMetadata: r.hasAi };
}

export function cleanBytes(format, u8) {
  if (format === "png") return stripPng(u8, { stripAllText: true });
  if (format === "jpeg") return stripJpeg(u8, { stripAllApp: true });
  if (format === "video") return cleanVideo(u8);
  return cleanContainer(format, u8);
}

export function cleanedFileName(name) {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return `${name}.cleaned`;
  return `${name.slice(0, dot)}.cleaned${name.slice(dot)}`;
}

export function downloadBytes(bytes, filename, mime = "application/octet-stream") {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function analyzeFile(file) {
  const u8 = new Uint8Array(await file.arrayBuffer());
  const format = detectFormat(file.name, u8);
  if (format === "unknown") return { format, error: "unsupported", sizeIn: u8.length };
  return { format, u8, name: file.name, sizeIn: u8.length, before: inspectBytes(format, u8) };
}

export async function cleanPrepared(prep, opts = {}) {
  const { format, u8 } = prep;
  let { bytes, actions } = cleanBytes(format, u8);
  if (opts.pixel && isRasterFormat(format)) {
    const res = await disruptPixels(bytes, format, opts.pixelStrength || "balanced");
    // Cleaning must stay the last step: the browser encoder re-adds its own
    // segments (JFIF/ICC) that we do not want in the output.
    bytes = cleanBytes(format, res.bytes).bytes;
    actions = [...actions, res.action];
  }
  const preview = isRasterFormat(format)
    ? {
        before: URL.createObjectURL(new Blob([u8], { type: MIME[format] })),
        after: URL.createObjectURL(new Blob([bytes], { type: MIME[format] })),
      }
    : null;
  return {
    format,
    before: prep.before,
    after: inspectBytes(format, bytes),
    actions,
    bytes,
    preview,
    sizeIn: prep.sizeIn,
    sizeOut: bytes.length,
    outName: cleanedFileName(prep.name),
  };
}

export async function processFile(file, opts = {}) {
  const prep = await analyzeFile(file);
  if (prep.error) return prep;
  return cleanPrepared(prep, opts);
}
